import {ConversationActionContextsEnum} from '../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {ConversationPatch} from '../../../libs/shared/core/src/models/api/conversationPatchModel';
import {
  CellsContext,
  CellsContextTypesEnum,
} from '../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  ConversationStatusesEnum,
  statusToTeamStatus,
  statusToTrackerStatus,
  UpdateConversationTagsOptions,
} from './conversationHelpers';

export function buildAddAndRemoveTagsConversationPatch(
  options: UpdateConversationTagsOptions,
): ConversationPatch {
  return {
    addTags: options.add || [],
    removeTags: options.remove || [],
  };
}

function buildSetStatusConversationPatches(
  teammateId: number,
  newStatus: ConversationStatusesEnum,
  options: SetStatusConversationPatchOptions = {},
) {
  // Build the tracker and team patches.
  const trackerPatch: ConversationPatch = {
    addTrackers: [
      {
        teammateId,
        status: statusToTrackerStatus(newStatus),
        // bump: true has the priority over any bumpedAt timestamp.
        bump: options.previousBumpedAt === undefined,
        bumpedAt: options.previousBumpedAt,
        remindAt: options.remindAt,
      },
    ],
  };

  const teamPatch: ConversationPatch = {
    remindAt: options.remindAt,
    teamStatus: options.newTicketStatusTagId ? undefined : statusToTeamStatus(newStatus),
    ticketStatusTagId: options.newTicketStatusTagId ? options.newTicketStatusTagId : undefined,
  };

  return {trackerPatch, teamPatch};
}

interface SetStatusConversationPatchOptions {
  newTicketStatusTagId?: number;
  previousBumpedAt?: number;
  remindAt?: number | null;
}

export function buildSetStatusConversationPatch(
  teammateId: number,
  conversationContext: ConversationActionContextsEnum,
  newStatus: ConversationStatusesEnum,
  options: SetStatusConversationPatchOptions = {},
) {
  const {trackerPatch, teamPatch} = buildSetStatusConversationPatches(teammateId, newStatus, options);

  // If the new status is "Destroyed", update the team status.
  if (newStatus === ConversationStatusesEnum.DESTROYED) {
    return teamPatch;
  }

  // If our teammate is the assignee, update both.
  if (conversationContext === ConversationActionContextsEnum.ASSIGNEE) {
    return {
      ...trackerPatch,
      ...teamPatch,
    };
  }

  // Otherwise, it depends on whether we're in a team context or not.
  return conversationContext === ConversationActionContextsEnum.TEAM ? teamPatch : trackerPatch;
}

export function buildDestroyConversationPatch(teammateId: number) {
  const {teamPatch} = buildSetStatusConversationPatches(teammateId, ConversationStatusesEnum.DESTROYED);
  return teamPatch;
}

/** Compute whether we should use the team patch for select all jobs. */
const teamPatchCellsContextTypes = [
  CellsContextTypesEnum.TEAM,
  CellsContextTypesEnum.MIXED_TEAM_TAG,
  CellsContextTypesEnum.CUSTOM_VIEW,
  CellsContextTypesEnum.MIXED_SEARCH_GLOBAL,
];

export function shouldUseTeamPatch(cellsContext: CellsContext) {
  return teamPatchCellsContextTypes.includes(cellsContext.type);
}
